import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, X } from "lucide-react";
import {
  type AccountOption,
  accountName,
  blockerTone,
  useAccountOptions,
} from "@/lib/broadcasts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Who posts: the company's accounts, each with whether it can post right now
 * and, if not, why — the worker's own gate, so the list does not promise what
 * the worker will not do. An account that is not ready may still be chosen:
 * it starts by itself once the reason is gone.
 */
export function AccountPicker({
  selected,
  onChange,
  disabled = false,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const options = useAccountOptions();
  const [query, setQuery] = useState("");

  const all = useMemo(() => {
    // Ready ones first; among equals, by number.
    const rows = [...(options.data ?? [])];
    rows.sort(
      (a, b) =>
        Number(a.blocker !== null) - Number(b.blocker !== null) || a.seq_no - b.seq_no,
    );
    return rows;
  }, [options.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((a) => accountName(a).toLowerCase().includes(q)) : all;
  }, [all, query]);

  const chosen = useMemo(() => all.filter((a) => selected.includes(a.id)), [all, selected]);
  const ready = all.filter((a) => a.blocker === null);

  const toggle = (account: AccountOption) =>
    onChange(
      selected.includes(account.id)
        ? selected.filter((id) => id !== account.id)
        : [...selected, account.id],
    );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              iconLeft={<Users size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {selected.length === 0
                ? t("broadcasts.accounts.choose")
                : t("broadcasts.accounts.n_chosen", { count: selected.length })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[26rem] p-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("broadcasts.accounts.search") ?? ""}
              className="mb-2 h-8"
            />
            <div className="max-h-72 space-y-0.5 overflow-y-auto">
              {options.isPending ? (
                <p className="px-2 py-3 text-center text-xs text-fg-muted">
                  {t("broadcasts.loading")}
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-fg-muted">
                  {all.length === 0
                    ? t("broadcasts.accounts.none_in_company")
                    : t("broadcasts.accounts.nothing_found")}
                </p>
              ) : (
                filtered.map((account) => (
                  <label
                    key={account.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-bg-secondary"
                  >
                    <Checkbox
                      checked={selected.includes(account.id)}
                      onCheckedChange={() => toggle(account)}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm">{accountName(account)}</span>
                    <Badge tone={blockerTone(account.blocker)} className="shrink-0">
                      {account.blocker === null
                        ? t("broadcasts.blocker.ready")
                        : t(`broadcasts.blocker.${account.blocker}`)}
                    </Badge>
                  </label>
                ))
              )}
            </div>
            {all.length > 0 && (
              <div className="mt-2 flex gap-2 border-t border-border pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  disabled={ready.length === 0}
                  onClick={() =>
                    onChange(Array.from(new Set([...selected, ...ready.map((a) => a.id)])))
                  }
                >
                  {t("broadcasts.accounts.choose_ready", { count: ready.length })}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  disabled={selected.length === 0}
                  onClick={() => onChange([])}
                >
                  {t("broadcasts.accounts.clear")}
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {all.length > 0 && (
          <span className="text-xs text-fg-muted">
            {t("broadcasts.accounts.ready_of", { ready: ready.length, total: all.length })}
          </span>
        )}
      </div>

      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chosen.map((account) => (
            <Badge
              key={account.id}
              tone={blockerTone(account.blocker)}
              dot
              className="max-w-[16rem] px-2 py-0.5"
              title={
                account.blocker === null
                  ? t("broadcasts.blocker.ready")
                  : t(`broadcasts.blocker.${account.blocker}`)
              }
            >
              <span className="truncate text-fg-primary">{accountName(account)}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={t("broadcasts.accounts.remove")}
                onClick={() => toggle(account)}
                className="ml-1 opacity-70 hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
AccountPicker.displayName = "AccountPicker";
